/**
 * Pure helpers for the score breakdown page.
 * Extracted from score.tsx to keep the page component focused on layout/state.
 */

import type { ScoreCriteria, ScoreDimension } from './types';
import type { SummaryStrength, SummaryWeakness } from '@/components/score/SummaryCard';
import type { ExportDimension, ExportScorecardPayload } from './export-scorecard-pdf';
import type { DealReadiness } from '@/lib/dataroom/utils';

// ── Dimension display names ──────────────────────────────────────────────

export const dimensionLabels: Record<ScoreDimension, string> = {
  team: 'Team',
  product: 'Product',
  market: 'Market',
  traction: 'Traction',
  defensibility: 'Defensibility',
  competition: 'Competition',
  'business-model': 'Business Model',
  timing: 'Timing',
  'vision-story': 'Vision & Story',
  'gtm-execution': 'GTM Execution',
  'financial-health': 'Financial Health',
  'execution-velocity': 'Execution Velocity',
  scalability: 'Scalability',
  'deal-attractiveness': 'Deal Attractiveness',
  'social-proof': 'Social Proof',
};

export function getDimensionLabel(c: ScoreCriteria): string {
  return dimensionLabels[c.dimension!] || c.name;
}

// ── Summary card narrative inputs ────────────────────────────────────────

export function buildSummaryStrengths(criteria: ScoreCriteria[]): SummaryStrength[] {
  return [...criteria]
    .filter((c) => c.score >= 70)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((c) => ({
      name: getDimensionLabel(c),
      score: c.score,
      detail: c.feedback,
    }));
}

export function buildSummaryWeaknesses(criteria: ScoreCriteria[]): SummaryWeakness[] {
  return [...criteria]
    .filter((c) => c.score < 60)
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map((c) => ({
      name: getDimensionLabel(c),
      score: c.score,
      detail: c.feedback,
    }));
}

// ── Deal Readiness ───────────────────────────────────────────────────────
// Real deal-readiness is computed from the startup's actual data-room checklist
// uploads — see `computeDealReadiness` in `@/lib/dataroom/utils`. The previous
// hardcoded MOCK_DD_DOCS / getDDStats (which rendered unconditionally on the
// score page, dashboard, and PDF exports) were removed; callers now pass the
// real `DealReadiness` in.

// ── PDF payload builder ─────────────────────────────────────────────────

export function dimensionToExportDim(c: ScoreCriteria): ExportDimension {
  return {
    name: getDimensionLabel(c),
    rawScore: Math.round((c.score / c.maxScore) * 100),
    feedback: c.feedback || '',
    evidenceLevel: c.evidenceLevel,
    riskLevel: c.riskLevel,
    recommendation: c.recommendation,
  };
}

export interface BuildExportPayloadInput {
  startupName: string;
  tagline?: string;
  description?: string;
  finalScore: number;
  ventureLabel: string;
  evidenceConfidence: number;
  sourceFlavor?: string;
  riskSummary: { high: number; medium: number; low: number };
  summaryNarrative: { overall: string; strengths: string; weaknesses: string } | null;
  summaryStrengths: SummaryStrength[];
  summaryWeaknesses: SummaryWeakness[];
  recommendations: string[];
  risks: ExportScorecardPayload['risks'];
  fundamentalsCriteria: ScoreCriteria[];
  growthCriteria: ScoreCriteria[];
  /** Real deal-readiness from the data-room checklist (computeDealReadiness). */
  dealReadiness: DealReadiness;
}

export function buildScorecardExportPayload(input: BuildExportPayloadInput): ExportScorecardPayload {
  const dd = input.dealReadiness;
  return {
    companyName: input.startupName,
    tagline: input.tagline,
    description: input.description,
    finalScore: input.finalScore,
    ventureLabel: input.ventureLabel,
    ventureScore: input.finalScore,
    evidenceConfidence: input.evidenceConfidence,
    sourceFlavor: input.sourceFlavor,
    riskSummary: input.riskSummary,
    dealReadinessPercent: dd.percent,
    dealReadinessPresent: dd.present,
    dealReadinessTotal: dd.total,
    narrativeOverall:
      input.summaryNarrative?.overall ??
      `${input.startupName} scores ${input.finalScore}/100 in the ${input.ventureLabel} tier.`,
    narrativeStrengths:
      input.summaryNarrative?.strengths ??
      input.summaryStrengths.map((s) => `${s.name} (${s.score}): ${s.detail ?? ''}`).join(' '),
    narrativeWeaknesses:
      input.summaryNarrative?.weaknesses ??
      input.summaryWeaknesses.map((w) => `${w.name} (${w.score}): ${w.detail ?? ''}`).join(' '),
    recommendations: input.recommendations,
    risks: input.risks,
    fundamentalsDims: input.fundamentalsCriteria.map(dimensionToExportDim),
    growthDims: input.growthCriteria.map(dimensionToExportDim),
    ddDocs: dd.docs,
  };
}
